import { useCallback, useEffect, useMemo, useState } from 'react';
import { STRINGS, type QuoteFormConfig } from '@maintenance-tracker/shared';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { PageHeader } from '@/components/ui/page-header';
import { api, ApiError, cn } from '@/lib/utils';
import { downloadBlob, qrToPngBlob, qrToSvg } from '@/lib/qr';

type Tab = 'inbox' | 'form' | 'share';

type FormPayload = {
  form: {
    id: string;
    slug: string;
    status: string;
    config: QuoteFormConfig;
    allowedOrigins: string[];
    retentionMonths: number;
    publicUrl: string;
    embedScript: string;
    embedIframe: string;
    updatedAt: string;
  };
  emailHealth: {
    status: string;
    addresses: Array<{ id: string; email: string; verifiedAt: string | null }>;
    lastSubmissionAt: string | null;
  };
  businessModelNote: string;
};

type Inquiry = {
  id: string;
  name: string;
  phone: string | null;
  email: string | null;
  service: string | null;
  zip: string | null;
  timing: string | null;
  status: string;
  outsideArea: boolean;
  photoCount: number;
  receivedAt: string;
  description?: string | null;
  notes?: string | null;
  closedReason?: string | null;
};

export function QuoteRequestsPage() {
  const [tab, setTab] = useState<Tab>('inbox');
  const [data, setData] = useState<FormPayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setError(null);
    try {
      const res = await api<FormPayload>('/api/v1/quotes/form');
      setData(res);
      if (res.form.status === 'draft' && res.form.config.wizardStep < 5) {
        setTab('form');
      }
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  if (loading) {
    return <p className="text-sm text-text-secondary">Loading Quote Requests…</p>;
  }
  if (error || !data) {
    return (
      <div className="space-y-3">
        <p className="text-sm text-danger">{error || 'Unavailable'}</p>
        <Button onClick={() => void refresh()}>Retry</Button>
      </div>
    );
  }

  const showWizard = data.form.status === 'draft';

  return (
    <div className="mx-auto max-w-4xl space-y-6">
      <PageHeader
        title="Quote Requests"
        description="Your form, page, and inquiries"
      />
      <p className="text-sm text-text-secondary">{data.businessModelNote}</p>

      <div className="flex flex-wrap gap-2 border-b border-border pb-2">
        {(
          [
            ['inbox', 'Inbox'],
            ['form', showWizard ? 'Setup' : 'Form and page'],
            ['share', 'Share and embed'],
          ] as const
        ).map(([id, label]) => (
          <button
            key={id}
            type="button"
            onClick={() => setTab(id)}
            className={cn(
              'rounded-lg px-3 py-2 text-sm font-medium',
              tab === id ? 'bg-bg-subtle text-text-primary' : 'text-text-secondary hover:bg-bg-subtle',
            )}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === 'inbox' && (
        <InboxTab
          form={data.form}
          emailHealth={data.emailHealth}
          onRefresh={refresh}
        />
      )}
      {tab === 'form' && (
        <FormTab data={data} onSaved={refresh} onPublished={() => { void refresh(); setTab('share'); }} />
      )}
      {tab === 'share' && <ShareTab data={data} onRefresh={refresh} />}
    </div>
  );
}

function FormTab({
  data,
  onSaved,
  onPublished,
}: {
  data: FormPayload;
  onSaved: () => Promise<void>;
  onPublished: () => void;
}) {
  const [config, setConfig] = useState(data.form.config);
  const [step, setStep] = useState(data.form.config.wizardStep || 1);
  const [notifyEmail, setNotifyEmail] = useState('');
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [origins, setOrigins] = useState(data.form.allowedOrigins.join(', '));
  const [retention, setRetention] = useState(data.form.retentionMonths);
  const [autosaveLabel, setAutosaveLabel] = useState<string | null>(null);

  const save = async (nextStep?: number, publish = false, quiet = false) => {
    if (!quiet) {
      setSaving(true);
      setMsg(null);
    }
    try {
      const wizardStep = nextStep ?? step;
      await api('/api/v1/quotes/form', {
        method: 'PATCH',
        body: JSON.stringify({
          config: { ...config, wizardStep },
          allowedOrigins: origins
            .split(',')
            .map((s) => s.trim())
            .filter(Boolean),
          retentionMonths: retention,
        }),
      });
      if (publish) {
        await api('/api/v1/quotes/form/publish', { method: 'POST', body: '{}' });
        setMsg(STRINGS.quote.published);
        onPublished();
      } else if (quiet) {
        setAutosaveLabel('Saved');
        setTimeout(() => setAutosaveLabel(null), 1500);
      } else {
        setMsg('Saved');
        if (nextStep) setStep(nextStep);
        await onSaved();
      }
    } catch (e) {
      if (!quiet) setMsg(e instanceof ApiError ? e.message : 'Save failed');
    } finally {
      if (!quiet) setSaving(false);
    }
  };

  // Autosave while editing (clearly indicated).
  useEffect(() => {
    if (data.form.status !== 'draft' && data.form.status !== 'published' && data.form.status !== 'paused') {
      return;
    }
    const t = window.setTimeout(() => {
      void save(undefined, false, true);
    }, 1200);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- debounce on form fields only
  }, [config, origins, retention, step]);

  const addNotify = async () => {
    if (!notifyEmail.trim()) return;
    try {
      await api('/api/v1/quotes/notify', {
        method: 'POST',
        body: JSON.stringify({ email: notifyEmail.trim() }),
      });
      setNotifyEmail('');
      setMsg(STRINGS.quote.notifyUnverified);
      await onSaved();
    } catch (e) {
      setMsg(e instanceof ApiError ? e.message : 'Could not add email');
    }
  };

  return (
    <div className="space-y-6">
      {data.form.status === 'draft' && (
        <p className="text-sm text-text-secondary">
          Step {step} of 5 — progress saves as you go
          {autosaveLabel ? ` · ${autosaveLabel}` : ''}.
        </p>
      )}
      {msg && <p className="text-sm text-text-secondary">{msg}</p>}
      {!msg && autosaveLabel && data.form.status !== 'draft' && (
        <p className="text-sm text-text-muted">{autosaveLabel}</p>
      )}

      {(step === 1 || data.form.status !== 'draft') && (
        <section className="space-y-3 rounded-2xl border border-border bg-bg-surface p-5">
          <h3 className="font-semibold text-text-primary">Business and contact</h3>
          <label className="block text-sm">
            Business name
            <Input
              className="mt-1"
              value={config.businessName}
              onChange={(e) => setConfig({ ...config, businessName: e.target.value })}
            />
          </label>
          <label className="block text-sm">
            Public phone
            <Input
              className="mt-1"
              value={config.publicPhone || ''}
              onChange={(e) => setConfig({ ...config, publicPhone: e.target.value })}
            />
          </label>
          <label className="block text-sm">
            Public reply email
            <Input
              className="mt-1"
              value={config.publicReplyEmail || ''}
              onChange={(e) => setConfig({ ...config, publicReplyEmail: e.target.value })}
            />
          </label>
          <div>
            <p className="text-sm font-medium">Notification emails (verified before send)</p>
            <ul className="mt-2 space-y-1 text-sm">
              {data.emailHealth.addresses.map((a) => (
                <li key={a.id} className="flex items-center justify-between gap-2">
                  <span>
                    {a.email}{' '}
                    <span className="text-text-muted">
                      {a.verifiedAt ? '· Verified' : '· Not verified'}
                    </span>
                  </span>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={async () => {
                      await api(`/api/v1/quotes/notify/${a.id}`, { method: 'DELETE' });
                      await onSaved();
                    }}
                  >
                    Remove
                  </Button>
                </li>
              ))}
            </ul>
            <div className="mt-2 flex gap-2">
              <Input
                placeholder="owner@example.com"
                value={notifyEmail}
                onChange={(e) => setNotifyEmail(e.target.value)}
              />
              <Button variant="secondary" onClick={() => void addNotify()}>
                Add
              </Button>
            </div>
          </div>
        </section>
      )}

      {(step === 2 || data.form.status !== 'draft') && (
        <section className="space-y-3 rounded-2xl border border-border bg-bg-surface p-5">
          <h3 className="font-semibold">Services offered (up to 15)</h3>
          <textarea
            className="min-h-28 w-full rounded-lg border border-border px-3 py-2 text-sm"
            value={config.services.join('\n')}
            onChange={(e) =>
              setConfig({
                ...config,
                services: e.target.value
                  .split('\n')
                  .map((s) => s.trim())
                  .filter(Boolean)
                  .slice(0, 15),
              })
            }
            placeholder="One service per line"
          />
        </section>
      )}

      {(step === 3 || data.form.status !== 'draft') && (
        <section className="space-y-3 rounded-2xl border border-border bg-bg-surface p-5">
          <h3 className="font-semibold">Service area</h3>
          <select
            className="w-full rounded-lg border border-border px-3 py-2 text-sm"
            value={config.serviceAreaMode}
            onChange={(e) =>
              setConfig({
                ...config,
                serviceAreaMode: e.target.value as QuoteFormConfig['serviceAreaMode'],
              })
            }
          >
            <option value="zip_check">Ask ZIP and flag outside area</option>
            <option value="city_only">Ask city only (no check)</option>
          </select>
          <textarea
            className="min-h-24 w-full rounded-lg border border-border px-3 py-2 text-sm"
            value={config.serviceAreaChips.join('\n')}
            onChange={(e) =>
              setConfig({
                ...config,
                serviceAreaChips: e.target.value
                  .split(/[\n,]/)
                  .map((s) => s.trim())
                  .filter(Boolean),
              })
            }
            placeholder="ZIPs or cities, one per line"
          />
        </section>
      )}

      {(step === 4 || data.form.status !== 'draft') && (
        <section className="space-y-3 rounded-2xl border border-border bg-bg-surface p-5">
          <h3 className="font-semibold">Customer questions</h3>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={config.fields.photosEnabled}
              onChange={(e) =>
                setConfig({ ...config, fields: { ...config.fields, photosEnabled: e.target.checked } })
              }
            />
            Allow photo uploads
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={config.fields.timingEnabled}
              onChange={(e) =>
                setConfig({ ...config, fields: { ...config.fields, timingEnabled: e.target.checked } })
              }
            />
            Preferred timing
          </label>
          <label className="block text-sm">
            Description prompt
            <Input
              className="mt-1"
              value={config.descriptionPrompt}
              onChange={(e) => setConfig({ ...config, descriptionPrompt: e.target.value })}
            />
          </label>
        </section>
      )}

      {(step === 5 || data.form.status !== 'draft') && (
        <section className="space-y-3 rounded-2xl border border-border bg-bg-surface p-5">
          <h3 className="font-semibold">Consent and wording</h3>
          <label className="block text-sm">
            Success message
            <Input
              className="mt-1"
              value={config.successMessage}
              onChange={(e) => setConfig({ ...config, successMessage: e.target.value })}
            />
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={config.fields.smsFollowUpWording}
              onChange={(e) =>
                setConfig({
                  ...config,
                  fields: { ...config.fields, smsFollowUpWording: e.target.checked },
                  consentPattern: e.target.checked ? 'withSms' : 'default',
                })
              }
            />
            Include SMS rates wording
          </label>
          <label className="block text-sm">
            Retention (months)
            <select
              className="mt-1 w-full rounded-lg border border-border px-3 py-2"
              value={retention}
              onChange={(e) => setRetention(Number(e.target.value))}
            >
              <option value={12}>12</option>
              <option value={24}>24</option>
              <option value={36}>36</option>
            </select>
          </label>
          <label className="block text-sm">
            Allowed embed domains (comma-separated)
            <Input className="mt-1" value={origins} onChange={(e) => setOrigins(e.target.value)} />
          </label>
        </section>
      )}

      <div className="flex flex-wrap gap-2">
        {data.form.status === 'draft' && step > 1 && (
          <Button variant="secondary" onClick={() => setStep(step - 1)}>
            Back
          </Button>
        )}
        {data.form.status === 'draft' && step < 5 && (
          <Button loading={saving} onClick={() => void save(step + 1)}>
            Save and continue
          </Button>
        )}
        {(data.form.status !== 'draft' || step === 5) && (
          <Button loading={saving} onClick={() => void save(5, false)}>
            Save
          </Button>
        )}
        {data.form.status === 'draft' && step === 5 && (
          <Button
            loading={saving}
            onClick={() => {
              if (confirm('Publish your quote page? A labeled self-test will be created (hidden from inbox).')) {
                void save(5, true);
              }
            }}
          >
            Publish
          </Button>
        )}
      </div>
    </div>
  );
}

function InboxTab({
  form,
  emailHealth,
  onRefresh,
}: {
  form: FormPayload['form'];
  emailHealth: FormPayload['emailHealth'];
  onRefresh: () => Promise<void>;
}) {
  const [rows, setRows] = useState<Inquiry[]>([]);
  const [q, setQ] = useState('');
  const [status, setStatus] = useState('');
  const [selected, setSelected] = useState<string | null>(null);
  const [detail, setDetail] = useState<Record<string, unknown> | null>(null);
  const [checked, setChecked] = useState<Set<string>>(new Set());

  const load = useCallback(async () => {
    const params = new URLSearchParams();
    if (q) params.set('q', q);
    if (status) params.set('status', status);
    const res = await api<{ inquiries: Inquiry[]; emailHealth: FormPayload['emailHealth'] }>(
      `/api/v1/quotes/inquiries?${params}`,
    );
    setRows(res.inquiries);
  }, [q, status]);

  useEffect(() => {
    void load().catch(() => undefined);
  }, [load]);

  useEffect(() => {
    if (!selected) {
      setDetail(null);
      return;
    }
    void api<{ inquiry: Record<string, unknown> }>(`/api/v1/quotes/inquiries/${selected}`)
      .then((r) => setDetail(r.inquiry))
      .catch(() => setDetail(null));
  }, [selected]);

  const healthLabel =
    emailHealth.status === 'failing'
      ? 'Email: Failing (check notification delivery)'
      : emailHealth.status === 'verified'
        ? 'Email: Verified'
        : emailHealth.status === 'not_verified'
          ? 'Email: Not verified (no emails will be sent)'
          : 'Email: No notification addresses';

  if (form.status === 'draft') {
    return (
      <div className="rounded-2xl border border-border bg-bg-surface p-6 text-center">
        <p className="text-sm text-text-secondary">{STRINGS.quote.inboxEmpty}</p>
        <p className="mt-2 text-sm text-text-muted">Finish setup and publish to start receiving inquiries.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3 text-sm text-text-secondary">
        <span>{healthLabel}</span>
        {emailHealth.lastSubmissionAt && (
          <span>Last submission received {new Date(emailHealth.lastSubmissionAt).toLocaleString()}</span>
        )}
      </div>

      {!rows.length && !q && !status ? (
        <div className="rounded-2xl border border-border bg-bg-surface p-6 text-center space-y-3">
          <p className="text-sm text-text-secondary">{STRINGS.quote.inboxEmpty}</p>
          <p className="text-sm break-all text-text-primary">{form.publicUrl}</p>
          <div className="flex justify-center gap-2">
            <Button
              variant="secondary"
              onClick={() => void navigator.clipboard.writeText(form.publicUrl)}
            >
              Copy link
            </Button>
            <a href={form.publicUrl} target="_blank" rel="noreferrer">
              <Button>Open page</Button>
            </a>
          </div>
        </div>
      ) : (
        <>
          <div className="flex flex-wrap gap-2">
            <Input
              placeholder="Search name, phone, text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="max-w-xs"
            />
            <select
              className="rounded-lg border border-border px-3 py-2 text-sm"
              value={status}
              onChange={(e) => setStatus(e.target.value)}
            >
              <option value="">All statuses</option>
              <option value="new">New</option>
              <option value="contacted">Contacted</option>
              <option value="closed">Closed</option>
              <option value="spam">Spam</option>
            </select>
            <Button variant="secondary" onClick={() => void load()}>
              Filter
            </Button>
            <a href="/api/v1/quotes/export.csv">
              <Button variant="secondary">Export CSV</Button>
            </a>
            {checked.size > 0 && (
              <Button
                variant="secondary"
                onClick={async () => {
                  await api('/api/v1/quotes/inquiries/bulk', {
                    method: 'POST',
                    body: JSON.stringify({ ids: [...checked], status: 'contacted' }),
                  });
                  setChecked(new Set());
                  await load();
                }}
              >
                Mark contacted ({checked.size})
              </Button>
            )}
          </div>

          <ul className="divide-y divide-border rounded-2xl border border-border bg-bg-surface">
            {rows.map((r) => (
              <li key={r.id} className="flex items-start gap-3 p-4">
                <input
                  type="checkbox"
                  checked={checked.has(r.id)}
                  onChange={(e) => {
                    const next = new Set(checked);
                    if (e.target.checked) next.add(r.id);
                    else next.delete(r.id);
                    setChecked(next);
                  }}
                />
                <button
                  type="button"
                  className="flex-1 text-left"
                  onClick={() => setSelected(r.id)}
                >
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-medium text-text-primary">{r.name}</span>
                    <StatusPill status={r.status} />
                    {r.outsideArea && (
                      <span className="text-xs text-text-muted">{STRINGS.quote.outsideArea}</span>
                    )}
                  </div>
                  <p className="mt-1 text-sm text-text-secondary">
                    {r.service} · {r.timing || '—'} · {r.zip || '—'} ·{' '}
                    {new Date(r.receivedAt).toLocaleString()}
                    {r.photoCount ? ` · ${r.photoCount} photo(s)` : ''}
                  </p>
                </button>
              </li>
            ))}
          </ul>
        </>
      )}

      {detail && (
        <InquiryDetail
          detail={detail as Inquiry & { photos?: Array<{ url: string | null }>; events?: unknown[] }}
          onClose={() => setSelected(null)}
          onChanged={async () => {
            await load();
            await onRefresh();
            if (selected) {
              const r = await api<{ inquiry: Record<string, unknown> }>(
                `/api/v1/quotes/inquiries/${selected}`,
              );
              setDetail(r.inquiry);
            }
          }}
        />
      )}
    </div>
  );
}

function StatusPill({ status }: { status: string }) {
  const colors: Record<string, string> = {
    new: 'bg-brand/10 text-brand',
    contacted: 'bg-bg-subtle text-text-secondary',
    closed: 'bg-bg-subtle text-text-muted',
    spam: 'bg-danger/10 text-danger',
  };
  return (
    <span className={cn('rounded-md px-2 py-0.5 text-xs font-medium capitalize', colors[status] || colors.new)}>
      {status}
    </span>
  );
}

function InquiryDetail({
  detail,
  onClose,
  onChanged,
}: {
  detail: Inquiry & { photos?: Array<{ url: string | null; id?: string }>; events?: unknown[]; customAnswers?: Record<string, string> };
  onClose: () => void;
  onChanged: () => Promise<void>;
}) {
  const [notes, setNotes] = useState(detail.notes || '');

  return (
    <div className="fixed inset-0 z-40 flex items-end justify-center bg-black/40 p-4 sm:items-center">
      <div className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-2xl bg-bg-surface p-5 shadow-card">
        <div className="flex items-start justify-between gap-2">
          <div>
            <h3 className="text-lg font-semibold">{detail.name}</h3>
            <StatusPill status={detail.status} />
          </div>
          <Button variant="ghost" size="sm" onClick={onClose}>
            Close
          </Button>
        </div>
        <dl className="mt-4 space-y-2 text-sm">
          <div>
            <dt className="text-text-muted">Service</dt>
            <dd>{detail.service}</dd>
          </div>
          <div>
            <dt className="text-text-muted">Contact</dt>
            <dd className="flex flex-wrap gap-2">
              {detail.phone && (
                <>
                  <a className="text-brand underline" href={`tel:${detail.phone}`}>
                    Call
                  </a>
                  <a className="text-brand underline" href={`sms:${detail.phone}`}>
                    Text
                  </a>
                </>
              )}
              {detail.email && (
                <a className="text-brand underline" href={`mailto:${detail.email}`}>
                  Email
                </a>
              )}
            </dd>
          </div>
          <div>
            <dt className="text-text-muted">Description</dt>
            <dd className="whitespace-pre-wrap">{detail.description}</dd>
          </div>
        </dl>
        {detail.photos && detail.photos.length > 0 && (
          <div className="mt-4 flex flex-wrap gap-2">
            {detail.photos.map((p, i) =>
              p.url ? (
                <a key={p.id || i} href={p.url} target="_blank" rel="noreferrer" className="text-sm text-brand underline">
                  Photo {i + 1}
                </a>
              ) : null,
            )}
          </div>
        )}
        <label className="mt-4 block text-sm">
          Private notes
          <textarea
            className="mt-1 min-h-20 w-full rounded-lg border border-border px-3 py-2"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </label>
        <div className="mt-4 flex flex-wrap gap-2">
          <Button
            size="sm"
            variant="secondary"
            onClick={async () => {
              await api(`/api/v1/quotes/inquiries/${detail.id}`, {
                method: 'PATCH',
                body: JSON.stringify({ status: 'contacted', notes }),
              });
              await onChanged();
            }}
          >
            Mark contacted
          </Button>
          <Button
            size="sm"
            variant="secondary"
            onClick={async () => {
              await api(`/api/v1/quotes/inquiries/${detail.id}`, {
                method: 'PATCH',
                body: JSON.stringify({ status: 'closed', closedReason: 'no_response', notes }),
              });
              await onChanged();
            }}
          >
            Close
          </Button>
          <Button
            size="sm"
            variant="destructive-outline"
            onClick={async () => {
              await api(`/api/v1/quotes/inquiries/${detail.id}`, {
                method: 'PATCH',
                body: JSON.stringify({ status: 'spam' }),
              });
              await onChanged();
              onClose();
            }}
          >
            Mark spam
          </Button>
          <a href={`/api/v1/quotes/inquiries/${detail.id}/export.pdf`}>
            <Button size="sm" variant="ghost">
              PDF
            </Button>
          </a>
          <Button
            size="sm"
            variant="destructive-outline"
            onClick={async () => {
              if (!confirm('Delete this inquiry?')) return;
              await api(`/api/v1/quotes/inquiries/${detail.id}`, { method: 'DELETE' });
              await onChanged();
              onClose();
            }}
          >
            Delete
          </Button>
        </div>
      </div>
    </div>
  );
}

function ShareTab({ data, onRefresh }: { data: FormPayload; onRefresh: () => Promise<void> }) {
  const url = data.form.publicUrl;
  const [copied, setCopied] = useState<string | null>(null);
  const svgPreview = useMemo(() => (url ? qrToSvg(url, 160) : ''), [url]);

  const copy = async (label: string, text: string) => {
    await navigator.clipboard.writeText(text);
    setCopied(label);
    setTimeout(() => setCopied(null), 1500);
  };

  if (data.form.status === 'draft') {
    return (
      <p className="text-sm text-text-secondary">Publish your form to get a share link and embed code.</p>
    );
  }

  return (
    <div className="space-y-6">
      <section className="space-y-3 rounded-2xl border border-border bg-bg-surface p-5">
        <h3 className="font-semibold">Hosted page</h3>
        <p className="break-all text-sm text-text-primary">{url}</p>
        <div className="flex flex-wrap gap-2">
          <Button variant="secondary" onClick={() => void copy('link', url)}>
            {copied === 'link' ? 'Copied' : 'Copy link'}
          </Button>
          <a href={url} target="_blank" rel="noreferrer">
            <Button variant="secondary">Open page</Button>
          </a>
          <Button
            variant="secondary"
            onClick={async () => {
              const blob = await qrToPngBlob(url, 1024);
              downloadBlob(blob, 'quote-qr.png');
            }}
          >
            Download QR PNG
          </Button>
          <Button
            variant="secondary"
            onClick={() => {
              downloadBlob(new Blob([qrToSvg(url, 1024)], { type: 'image/svg+xml' }), 'quote-qr.svg');
            }}
          >
            Download QR SVG
          </Button>
          <a href="/api/v1/quotes/share/flyer.pdf">
            <Button variant="secondary">Print flyer PDF</Button>
          </a>
          <Button
            variant="secondary"
            onClick={async () => {
              await api('/api/v1/quotes/form/pause', {
                method: 'POST',
                body: JSON.stringify({ paused: data.form.status !== 'paused' }),
              });
              await onRefresh();
            }}
          >
            {data.form.status === 'paused' ? 'Resume page' : 'Pause page'}
          </Button>
        </div>
        {svgPreview && (
          <div
            className="inline-block rounded-lg border border-border p-2"
            dangerouslySetInnerHTML={{ __html: svgPreview }}
          />
        )}
      </section>

      <section className="space-y-3 rounded-2xl border border-border bg-bg-surface p-5">
        <h3 className="font-semibold">Embed</h3>
        <label className="block text-sm">
          Script
          <textarea
            readOnly
            className="mt-1 min-h-20 w-full rounded-lg border border-border px-3 py-2 font-mono text-xs"
            value={data.form.embedScript}
          />
        </label>
        <Button size="sm" variant="secondary" onClick={() => void copy('script', data.form.embedScript)}>
          {copied === 'script' ? 'Copied' : 'Copy script'}
        </Button>
        <label className="block text-sm">
          iframe
          <textarea
            readOnly
            className="mt-1 min-h-20 w-full rounded-lg border border-border px-3 py-2 font-mono text-xs"
            value={data.form.embedIframe}
          />
        </label>
        <Button size="sm" variant="secondary" onClick={() => void copy('iframe', data.form.embedIframe)}>
          {copied === 'iframe' ? 'Copied' : 'Copy iframe'}
        </Button>
        <div className="text-sm text-text-secondary space-y-1">
          <p className="font-medium text-text-primary">Platform tips</p>
          <p>WordPress: Custom HTML block → paste script or iframe.</p>
          <p>Wix / Squarespace / GoDaddy / Webflow: Embed / HTML element → paste iframe.</p>
          <p>Other: any page that allows iframe or script tags.</p>
        </div>
      </section>

      <section className="space-y-2 rounded-2xl border border-border bg-bg-surface p-5 text-sm">
        <h3 className="font-semibold">Link ideas</h3>
        {[
          ['Google Business Profile', 'Add as your Appointment link'],
          ['Email signature', `Request an estimate: ${url}`],
          ['Text replies', `Here's our estimate form: ${url}`],
          ['Truck / invoices', url],
        ].map(([label, line]) => (
          <div key={label} className="flex flex-wrap items-center justify-between gap-2">
            <span>
              <strong>{label}:</strong> {line}
            </span>
            <Button size="sm" variant="ghost" onClick={() => void copy(label, line)}>
              Copy
            </Button>
          </div>
        ))}
      </section>
    </div>
  );
}
